<template>
    <div class="bg-surface-50 dark:bg-surface-900 flex flex-col overflow-hidden h-full">
        <div class="bg-surface-50 dark:bg-surface-950">
            <ul class="bg-surface-0 dark:bg-surface-900 p-0 m-0 list-none flex overflow-x-auto select-none">
                <template v-for="(item, index) of tabs" :key="index">
                    <li>
                        <a
                            class="cursor-pointer pl-6 pr-6 pt-4 pb-4 flex items-center border-b-2 border-solid transition-colors duration-150"
                            :class="{
                                'border-primary dark:border-primary text-primary hover:border-primary ':
                                    active === index,
                                'text-surface-700 dark:text-surface-0/80 border-transparent hover:border-surface':
                                    active !== index,
                            }"
                            @click="setOption(index)"
                        >
                            <i :class="[item.icon, '!text-xl', '!leading-none']" class="mr-2" />
                            <span class="font-medium">{{ item.label }}</span>
                        </a>
                    </li>
                </template>
            </ul>
        </div>

        <div class="h-full w-full overflow-hidden">
            <TreeTable :value="nodes" scrollable scrollHeight="flex" tableStyle="h-full w-full">
                <template #header>
                    <div class="flex flex-wrap items-center justify-end gap-2">
                        <Button
                            v-if="authService.hasPermission(PermissionsEnum.createOption)"
                            icon="pi pi-plus"
                            :label="!tabs[active].single ? 'New Group' : 'New Item'"
                            severity="contrast"
                            @click="createGroup"
                        />
                    </div>
                </template>
                <Column header="Name" expander style="width: 250px">
                    <template #body="{ node }">
                        <span v-if="node.data.parent">
                            <p class="text-lg font-bold">{{ node.data.label }}</p>
                        </span>
                        <span v-else>
                            <p class="text-md">{{ node.data.label }}</p></span
                        >
                    </template>
                </Column>

                <Column style="width: 150px">
                    <template #body="{ node }">
                        <Button
                            v-if="
                                node.data.parent &&
                                !tabs[active].single &&
                                authService.hasPermission(PermissionsEnum.createOption)
                            "
                            size="small"
                            icon="pi pi-plus"
                            v-tooltip="'Add item'"
                            severity="contrast"
                            @click="addItem(node.key)"
                        />

                        <Button
                            v-if="authService.hasPermission(PermissionsEnum.editOption)"
                            size="small"
                            icon="pi pi-pencil"
                            v-tooltip="'Update name'"
                            severity="contrast"
                            @click="updateGroupName(node)"
                        />

                        <ToggleSwitch
                            v-if="authService.hasPermission(PermissionsEnum.editOption)"
                            :modelValue="isActive(node)"
                            @change="(event) => statusUpdate(event, node)"
                        />
                    </template>
                </Column>
            </TreeTable>
        </div>
    </div>

    <SingleInputDialog
        v-if="showDialog"
        :title="dialogConfig.title"
        :okLabel="dialogConfig.label"
        v-model:visible="showDialog"
        :save-action="dialogConfig.action"
        :loading="loading"
    ></SingleInputDialog>
</template>
<script setup>
import { ref, onMounted, inject, computed } from "vue";
import { Button } from "primevue";
import TreeTable from "primevue/treetable";
import Column from "primevue/column";
import { OptionType } from "@/core/enums/OptionTypeEnum";
import SingleInputDialog from "@/shared/common/SingleInputDialog.vue";
import ToggleSwitch from "primevue/toggleswitch";
import debounce from "lodash/debounce";
import { useToast } from "primevue/usetoast";
import { PermissionsEnum } from "@/core/enums/PermissionsEnum";

const optionsService = inject("optionsService");
const authService = inject("authService");

const toast = useToast();

const nodes = ref();
const showDialog = ref(false);
const dialogConfig = ref();
const loading = ref(false);

const active = ref(0);
const tabs = ref([
    { label: "Industries", icon: "pi pi-building", value: OptionType.industry, single: false },
    { label: "Products", icon: "pi pi-tags", value: OptionType.product, single: true },
]);

onMounted(() => {
    loadOptions();
});

const isActive = (node) => {
    return node.data.status == "active";
};

const loadOptions = async () => {
    const optionName = tabs.value[active.value].value;

    const response = await optionsService.getByType(optionName, ["active", "inactive"]);
    nodes.value = parseToNodes(response.data);
};

const setOption = (index) => {
    active.value = index;
    loadOptions();
};

const createGroup = () => {
    const isSingle = tabs.value[active.value].single;
    if (isSingle) {
        const parentId = nodes.value[0].key;

        dialogConfig.value = createGroupItemDialog(parentId);
    } else {
        dialogConfig.value = createGroupDialog;
    }
    showDialog.value = true;
};

const updateGroupName = (node) => {
    dialogConfig.value =
        node.data.parent == true
            ? updateGroupDialog(node.key, node.data.label)
            : updateGroupItemDialog(node.data.label, node.data.group, node.key);

    showDialog.value = true;
};

const addItem = (id) => {
    dialogConfig.value = createGroupItemDialog(id);
    showDialog.value = true;
};

const createGroupAction = async (text) => {
    try {
        loading.value = true;
        const response = await optionsService.createGroup(tabs.value[active.value].value, text);
        await loadOptions();
    } catch (error) {
        throw error;
    } finally {
        loading.value = false;
    }
};

const updateGroupNameAction = async (text) => {
    try {
        loading.value = true;
        const response = await optionsService.updateGroup(dialogConfig.value.itemId, { name: text });
        await loadOptions();

        toast.add({
            severity: "info",
            summary: "Group name updated!",
            life: 4000,
        });
    } catch (error) {
        throw error;
    } finally {
        loading.value = false;
    }
};

const createGroupItemAction = async (text) => {
    try {
        loading.value = true;
        const response = await optionsService.createGroupItem(dialogConfig.value.itemId, text);
        await loadOptions();

        toast.add({
            severity: "info",
            summary: "Group item created",
            life: 4000,
        });
    } catch (error) {
        throw error;
    } finally {
        loading.value = false;
    }
};

const updateGroupItemNameAction = async (text) => {
    try {
        loading.value = true;
        const response = await optionsService.updateGroupItem(dialogConfig.value.groupId, dialogConfig.value.itemId, {
            name: text,
        });
        await loadOptions();

        toast.add({
            severity: "info",
            summary: "Group item name updated!",
            life: 4000,
        });
    } catch (error) {
        throw error;
    } finally {
        loading.value = false;
    }
};

const createGroupDialog = {
    action: createGroupAction,
    label: "Save",
    title: "Enter the new group",
};

const updateGroupDialog = (id, name) => {
    return {
        action: updateGroupNameAction,
        label: "Update",
        title: `Updating ${name}`,
        itemId: id,
    };
};

const createGroupItemDialog = (id) => {
    return {
        action: createGroupItemAction,
        label: "Save",
        title: "Enter the new group item title",
        itemId: id,
    };
};

const updateGroupItemDialog = (name, groupId, itemId) => {
    return {
        action: updateGroupItemNameAction,
        label: "Update",
        title: `Updating ${name}`,
        groupId,
        itemId,
    };
};

const parseToNodes = (data) => {
    return data.map((item) => {
        return {
            key: item.id,
            data: {
                label: item.label,
                status: item.status,
                parent: true,
            },
            children: item.items.map((subitem) => {
                return {
                    key: subitem.value,
                    data: {
                        group: item.id,
                        label: subitem.label,
                        status: subitem.status,
                    },
                };
            }),
        };
    });
};

const statusUpdate = debounce(async (event, node) => {
    try {
        if (node.data.parent) {
            await optionsService.updateGroup(node.key, {
                status: node.data.status == "active" ? "inactive" : "active",
            });

            toast.add({
                severity: "info",
                summary: "Option updated",
                life: 4000,
            });
        } else {
            await optionsService.updateGroupItem(node.data.group, node.key, {
                status: node.data.status == "active" ? "inactive" : "active",
            });
        }
    } catch (error) {}
}, 500);
</script>
